/**
 * The owner's morning report — "what happened yesterday", in one screen.
 *
 * Admin-only (the route is gated in `App.tsx` and the API enforces `requireRole('admin')`). It leads
 * with the net result for the day framed the way an owner reads it ("we made / we lost"), then the
 * sports that drove it, then the players behind it with whether each withdrew or kept playing.
 *
 * Two states get explicit, non-alarming handling because they are normal, not bugs:
 *  - The Snowflake token being expired/unauthorised (`service_unavailable`) → a reconnect banner that
 *    names the fix, since this is the failure that silently broke analytics before.
 *  - A quiet day with no activity → a plain "no activity" note rather than a wall of zeros.
 */
import type { OwnerDailyReport, OwnerPlayerRow, PlayerDayStatus } from '@apuestapr/contracts';
import { Button, icons } from '@apuestapr/ui';
import { useEffect, useMemo, useState, type ReactElement } from 'react';

import type { ApiFailure } from '../api/client.ts';
import { useSessionContext } from '../auth/SessionProvider.tsx';
import { createOwnerReportApi } from './owner-report-api.ts';

const currency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const number = (n: number): string => new Intl.NumberFormat('en-US').format(n);
const percent = (fraction: number): string => `${(fraction * 100).toFixed(1)}%`;

/** Yesterday in YYYY-MM-DD. The report's default day — an owner's "what happened overnight". */
const yesterday = (): string => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().slice(0, 10);
};

const prettyDate = (iso: string): string =>
  new Date(`${iso}T00:00:00`).toLocaleDateString('es-PR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

/**
 * The house's net result is positive when the house won. The owner reads it as their own outcome, so
 * a positive house result is framed green ("Ganancia") and a negative one red ("Pérdida").
 */
const resultTone = (houseResult: number): string =>
  houseResult >= 0 ? 'text-foreground' : 'text-destructive';

const statusLabels: Readonly<Record<PlayerDayStatus, string>> = {
  withdrew: 'Retiró dinero',
  kept_playing: 'Siguió jugando',
  deposited_more: 'Depositó más',
  settled: 'Cerró el día',
  data_pending: 'Sin datos aún',
};

const statusTone: Readonly<Record<PlayerDayStatus, string>> = {
  withdrew: 'bg-destructive/10 text-destructive',
  kept_playing: 'bg-secondary text-secondary-foreground',
  deposited_more: 'bg-muted text-foreground',
  settled: 'bg-muted text-muted-foreground',
  data_pending: 'bg-muted text-muted-foreground italic',
};

const KpiCard = ({
  label,
  value,
  tone = 'text-foreground',
  subtitle,
}: {
  readonly label: string;
  readonly value: string;
  readonly tone?: string;
  readonly subtitle?: string;
}): ReactElement => (
  <div className="rounded-xl border border-border bg-card p-4 shadow-raised">
    <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`mt-1 font-display text-2xl font-bold ${tone}`}>{value}</p>
    {subtitle !== undefined ? (
      <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
    ) : null}
  </div>
);

/** The reconnect banner for an expired Snowflake token — the specific, recoverable failure. */
const ReconnectBanner = ({ message }: { readonly message: string }): ReactElement => (
  <div className="rounded-xl border border-destructive/40 bg-destructive/5 p-4" role="alert">
    <p className="m-0 font-medium text-destructive">No se pudo conectar con Snowflake</p>
    <p className="m-0 mt-1 text-sm text-muted-foreground">{message}</p>
  </div>
);

const PlayersTable = ({
  players,
  depositDataAvailable,
}: {
  readonly players: readonly OwnerPlayerRow[];
  readonly depositDataAvailable: boolean;
}): ReactElement => (
  <div className="overflow-x-auto rounded-xl border border-border">
    <table className="w-full text-sm">
      <thead className="bg-muted/50">
        <tr>
          <th className="px-3 py-2 text-left font-medium">Jugador</th>
          <th className="px-3 py-2 text-left font-medium">Categoría</th>
          <th className="px-3 py-2 text-right font-medium">Apostó</th>
          <th className="px-3 py-2 text-right font-medium">Cobró</th>
          <th className="px-3 py-2 text-right font-medium">Resultado casa</th>
          <th className="px-3 py-2 text-right font-medium">Depositó</th>
          <th className="px-3 py-2 text-right font-medium">Retiró</th>
          <th className="px-3 py-2 text-left font-medium">Estado</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-border">
        {players.map((player) => (
          <tr key={player.playerRef} className="transition-colors hover:bg-muted/30">
            <td className="px-3 py-2 font-medium">
              {player.label}
              <span className="ml-1 text-xs text-muted-foreground">#{player.playerRef}</span>
            </td>
            <td className="px-3 py-2 text-muted-foreground">{player.category ?? '—'}</td>
            <td className="px-3 py-2 text-right">{currency(player.staked)}</td>
            <td className="px-3 py-2 text-right">{currency(player.returned)}</td>
            <td className={`px-3 py-2 text-right font-semibold ${resultTone(player.houseResult)}`}>
              {currency(player.houseResult)}
            </td>
            {/* When the feed has not loaded, the amounts are not real zeros — show a dash rather than
                $0.00 so the table does not assert money movement it cannot see. */}
            <td className="px-3 py-2 text-right">
              {depositDataAvailable ? currency(player.deposited) : '—'}
            </td>
            <td className="px-3 py-2 text-right">
              {depositDataAvailable ? currency(player.withdrawn) : '—'}
            </td>
            <td className="px-3 py-2">
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusTone[player.status]}`}
              >
                {statusLabels[player.status]}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

type Loaded =
  | { readonly status: 'ready'; readonly report: OwnerDailyReport }
  | { readonly status: 'error'; readonly failure: ApiFailure };

type LoadState = { readonly status: 'loading' } | Loaded;

export const OwnerReportPage = (): ReactElement => {
  const session = useSessionContext();
  const authenticated = session.state.status === 'authenticated' ? session.state.session : null;
  const api = useMemo(() => createOwnerReportApi(session.client), [session.client]);

  const [date, setDate] = useState(yesterday());
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      // Set inside the async callback, not the synchronous effect body, so React does not cascade a
      // render — the same shape `AnalyticsPage` uses for its own loader.
      setLoading(true);
      const result = await api.getDaily(date);
      if (cancelled) return;
      setLoaded(
        result.ok
          ? { status: 'ready', report: result.data }
          : { status: 'error', failure: result.failure },
      );
      setLoading(false);
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [api, date, refreshKey]);

  const state: LoadState = loading || loaded === null ? { status: 'loading' } : loaded;

  const headline = state.status === 'ready' ? state.report.headline : null;

  // A 503 from the API is the Snowflake token being expired/unauthorised — the reconnect case.
  const reconnect =
    state.status === 'error' && state.failure.kind === 'http' && state.failure.status === 503
      ? state.failure.message
      : null;

  if (!authenticated) return <></>;

  return (
    <section aria-labelledby="owner-report-heading" className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 id="owner-report-heading" className="font-display text-2xl font-bold">
            Resumen del día
          </h1>
          <p className="text-muted-foreground">{prettyDate(date)}</p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={date}
            max={yesterday()}
            onChange={(event) => setDate(event.currentTarget.value)}
            className="rounded-lg border border-border bg-card px-2 py-1 text-sm"
          />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setRefreshKey((k) => k + 1)}
            disabled={state.status === 'loading'}
          >
            <icons.refresh className="h-4 w-4" />
            {state.status === 'loading' ? 'Cargando…' : 'Actualizar'}
          </Button>
        </div>
      </div>

      {reconnect !== null ? <ReconnectBanner message={reconnect} /> : null}

      {state.status === 'error' && reconnect === null ? (
        <p role="alert">No se pudo cargar el resumen. Inténtalo de nuevo.</p>
      ) : null}

      {state.status === 'loading' ? (
        <div className="flex items-center justify-center py-10">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-muted border-t-primary" />
        </div>
      ) : null}

      {state.status === 'ready' ? (
        <>
          {headline === null ? (
            <p className="text-muted-foreground">
              No hay actividad registrada para este día todavía.
            </p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <KpiCard
                label="Resultado neto"
                value={currency(headline.netResult)}
                tone={resultTone(headline.netResult)}
                subtitle={headline.netResult >= 0 ? 'Ganancia de la casa' : 'Pérdida de la casa'}
              />
              <KpiCard label="Manejo (handle)" value={currency(headline.handle)} />
              <KpiCard label="Pagado a jugadores" value={currency(headline.payout)} />
              <KpiCard
                label="Margen (hold)"
                value={percent(headline.hold)}
                subtitle={`${number(headline.activePlayers)} jugadores activos`}
              />
            </div>
          )}

          <div className="space-y-3">
            <h2 className="m-0 text-lg font-semibold">En qué se ganó o perdió</h2>
            {state.report.topDrivers.length === 0 ? (
              <p className="text-muted-foreground">Sin desglose por deporte para este día.</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Deporte</th>
                      <th className="px-3 py-2 text-left font-medium">Liga</th>
                      <th className="px-3 py-2 text-right font-medium">Manejo</th>
                      <th className="px-3 py-2 text-right font-medium">Pagado</th>
                      <th className="px-3 py-2 text-right font-medium">Resultado casa</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {state.report.topDrivers.map((driver, index) => (
                      <tr
                        key={`${driver.sport}-${driver.league ?? ''}-${String(index)}`}
                        className="transition-colors hover:bg-muted/30"
                      >
                        <td className="px-3 py-2 font-medium">{driver.sport}</td>
                        <td className="px-3 py-2 text-muted-foreground">{driver.league ?? '—'}</td>
                        <td className="px-3 py-2 text-right">{currency(driver.turnover)}</td>
                        <td className="px-3 py-2 text-right">{currency(driver.paidOut)}</td>
                        <td
                          className={`px-3 py-2 text-right font-semibold ${resultTone(driver.houseResult)}`}
                        >
                          {currency(driver.houseResult)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <h2 className="m-0 text-lg font-semibold">Jugadores del día</h2>
            {!state.report.depositDataAvailable ? (
              <div
                className="rounded-xl border border-border bg-muted/40 p-3 text-sm text-muted-foreground"
                role="note"
              >
                Los datos de depósitos y retiros aún no se han cargado para este día, así que no se
                puede saber si cada jugador retiró o siguió jugando. Las columnas de depósito y
                retiro se muestran en blanco y el estado aparece como “Sin datos aún”. Las apuestas
                y el resultado del día sí son correctos.
              </div>
            ) : null}
            {state.report.players.length === 0 ? (
              <p className="text-muted-foreground">Sin actividad de jugadores para este día.</p>
            ) : (
              <PlayersTable
                players={state.report.players}
                depositDataAvailable={state.report.depositDataAvailable}
              />
            )}
          </div>
        </>
      ) : null}
    </section>
  );
};
